import {
  action, computed, makeAutoObservable, observable,
} from "mobx";
import { ICompanyPmMonthlySnapshot } from "src/types/ICompanyPmMonthlySnapshot";
import { IRootStore } from "../types/IRootStore";
import * as pmMonthlySnapshotsService from "../requests/pmMonthlySnapshots";

export enum PmProgressTab {
  USERS = "users",
  COMPANIES = "companies",
}

export class PmMonthlySnapshotStore {
  rootStore;

  @observable snapshots: ICompanyPmMonthlySnapshot[] = [];

  @observable isLoading = false;

  @observable activeTab = PmProgressTab.USERS;

  constructor(rootStore: IRootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  @computed
  get snapshotTotals(): Record<string, number> {
    return this.snapshots.reduce((acc: Record<string, number>, snapshot) => {
      acc.hotLeadCount += snapshot.attributes.hotLeadCount;
      acc.leadsChasedCount += snapshot.attributes.leadsChasedCount;
      acc.phoneCallCount += snapshot.attributes.phoneCallCount;
      acc.qualifiedLeadCount += snapshot.attributes.qualifiedLeadCount;

      return acc;
    }, {
      hotLeadCount: 0,
      leadsChasedCount: 0,
      phoneCallCount: 0,
      qualifiedLeadCount: 0,
    });
  }

  @computed
  get snapshotsGroupedByUser(): Record<string, ICompanyPmMonthlySnapshot[]> {
    return this.snapshots.reduce((acc: Record<string, ICompanyPmMonthlySnapshot[]>, snapshot) => {
      const userId = snapshot.attributes.userIds[0].toString();
      if (!acc[userId]) {
        acc[userId] = [];
      }
      acc[userId].push(snapshot);
      return acc;
    }, {});
  }

  @computed
  get snapshotsGroupedByCompany(): Record<string, ICompanyPmMonthlySnapshot[]> {
    return this.snapshots.reduce((acc: Record<string, ICompanyPmMonthlySnapshot[]>, snapshot) => {
      const companyId = snapshot.attributes.company.id;
      if (!acc[companyId]) {
        acc[companyId] = [];
      }
      acc[companyId].push(snapshot);
      return acc;
    }, {});
  }

  @action
  setActiveTab(activeTab: PmProgressTab) {
    this.activeTab = activeTab;
  }

  @action
  setSnapshots(snapshots: ICompanyPmMonthlySnapshot[]) {
    this.snapshots = snapshots;
  }

  @action
  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  async getPmMonthlySnapshotsAsync(yearMonth?: string) {
    this.setIsLoading(true);

    const snapshots = await pmMonthlySnapshotsService.getPmMonthlySnapshots({
      month: yearMonth,
    });
    this.setSnapshots(snapshots);

    this.setIsLoading(false);
  }
}

export default PmMonthlySnapshotStore;
